import { FC, useEffect, useState } from 'react';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import useSwr from 'swr';
import toast from 'react-hot-toast';
import ic_arr from '@images/right-arrow-long.png';
import { fetchPollDetails } from '@utils/Poll';
import { useAuth } from '@hooks/Auth';
import { format } from 'date-fns';
import ButtonMighty from '@components/elements/ButtonMighty';
import HeaderImage from '@components/elements/HeaderImage';
import Embed from '@components/elements/Embed';
import PeriodTimeline from '@components/elements/Period/Timeline';
import PollShareCard from '@components/modules/PollShareCard';
import PollOptionsForm from '@components/modules/Poll/PollOptionsForm';
import Logo from '@images/logo.png';

import './PollDetailPage.css';
import { isFuture, isPast, isWithinInterval } from 'date-fns';
import { CompanyRole } from '@sdk/API';
import InputText from '@components/elements/InputText';
import Loader from '@components/elements/Loader';

import { useGlobalContext } from '@context/customContext';
import defaultTheme from '@theme/defaultTheme';
import kidsTheme from '@theme/kidsTheme';
import hipsterTheme from '@theme/hipsterTheme';
import professionalTheme from '@theme/professionalTheme';
import InputTextarea from '@components/elements/InputTextarea';
import { createPollComment } from '@sdk/managers/PollsManager';
const THEMES = ['default', 'kids', 'hipster', 'professional'];
const randomColors = [
  '#2EFFE4',
  '#FF46FF',
  '#FFB400',
  '#FF2300',
  '#FFFF00',
  '#8401FF',
  '#28FF2F',
].filter((item) => item !== localStorage.getItem('randomColor'))[
  Math.floor(Math.random() * (localStorage.getItem('randomColor') ? 6 : 7))
];
localStorage.setItem('randomColor', randomColors);
const PollDetailPage: FC = () => {
  const history = useHistory();
  const {
    url,
    params: { pollId },
  } = useRouteMatch<{
    pollId: string;
  }>();

  const { user, dbUser, company, loading } = useAuth();
  const [auth, setAuth] = useState<boolean>(true);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>();
  const [isThemeCompany, setIsThemeCompany] = useState<boolean>(false);
  const [voteCount, setVoteCount] = useState(0);
  const { themeData, setThemeData, customTheme } = useGlobalContext();

  let { data, error } = useSwr(
    pollId && loading === false
      ? ['/poll/detail', pollId, user === null]
      : null,
    fetchPollDetails,
    {
      revalidateOnFocus: true,
    }
  );

  useEffect(() => {
    if (data) {
      console.log(data);
      const themeID = data?.poll.themeID || 'default';
      setVoteCount(
        data.poll.voteCount +
          (data.subPolls
            ? data.subPolls.reduce((r, a) => {
                r += a.voteCount;
                return r;
              }, 0)
            : 0)
      );
      if (themeID && THEMES.includes(themeID)) {
        setIsThemeCompany(false);
        switch (themeID) {
          case 'default':
            setThemeData(defaultTheme);
            break;
          case 'kids':
            setThemeData(kidsTheme);
            break;
          case 'hipster':
            setThemeData(hipsterTheme);
            break;
          case 'professional':
            setThemeData(professionalTheme);
            break;
          default:
            setThemeData(defaultTheme);
            break;
        }
      }
      if (themeID && customTheme.id === themeID) {
        setIsThemeCompany(true);
        const tempTheme = { colors: { ...defaultTheme.colors } };
        if (customTheme) {
          const themeAttributes = customTheme.attributes;
          if (themeAttributes) {
            const {
              pollActivePeriodBgColor,
              pollActivePeriodShadowColor,
              pollQuestionCardBgColor,
              pollQuestionCardQuestionColor,
              pollQuestionCardAnswerColor,
              pollPledgeCastBtnTextColor,
              pollPledgeCastBtnBgColor,
              pollPledgeCastBtnBracketColor,
              pollPageBgColor,
            } = themeAttributes;
            tempTheme.colors['poll-period-active-bgColor'] =
              pollActivePeriodBgColor ||
              defaultTheme.colors['poll-period-active-bgColor'];
            tempTheme.colors['poll-period-active-shadowColor'] =
              pollActivePeriodShadowColor ||
              defaultTheme.colors['poll-period-active-shadowColor'];

            tempTheme.colors['poll-questionCard-bgColor'] =
              pollQuestionCardBgColor ||
              defaultTheme.colors['poll-questionCard-bgColor'];
            tempTheme.colors['poll-questionCard-question-color'] =
              pollQuestionCardQuestionColor ||
              defaultTheme.colors['poll-questionCard-question-color'];
            tempTheme.colors['poll-questionCard-answer-color'] =
              pollQuestionCardAnswerColor ||
              defaultTheme.colors['poll-questionCard-answer-color'];

            tempTheme.colors['poll-btnPledgeCastVote-textColor'] =
              pollPledgeCastBtnTextColor ||
              defaultTheme.colors['poll-btnPledgeCastVote-textColor'];
            tempTheme.colors['poll-btnPledgeCastVote-color'] =
              pollPledgeCastBtnBgColor ||
              defaultTheme.colors['poll-btnPledgeCastVote-color'];
            tempTheme.colors['poll-btnPledgeCastVote-borderColor'] =
              pollPledgeCastBtnBracketColor ||
              defaultTheme.colors['poll-btnPledgeCastVote-borderColor'];
            tempTheme.colors['poll-page-bgColor'] =
              pollPageBgColor || defaultTheme.colors['poll-page-bgColor'];

            setThemeData({ ...tempTheme });
          }
        }
      }
    }
    return () => {
      setThemeData({ ...defaultTheme });
    };
  }, [customTheme, data, setThemeData]);

  useEffect(() => {
    if (error) {
      toast.error(error.message || 'Failed to fetch poll information.');
      console.error({ error });
    }
  }, [error]);

  const ownerPermission =
    user?.username === data?.poll.owner ||
    (company &&
      company.companyID === data?.poll?.companyID &&
      (company?.role === CompanyRole.ADMIN ||
        company.role === CompanyRole.REPRESENTATIVE));

  useEffect(() => {
    if (!data) return;

    const isOwner = data.poll.owner === dbUser?.username;
    const inCompany = data.poll.companyID === company?.companyID;
    const hasPassword =
      (data.poll.password === undefined || data.poll.password === null) ===
      false;

    if (isOwner || hasPassword === false) {
      setAuth(true);
    } else {
      setAuth(false);
    }

    // Enterprise poll
    if (data.poll.private === 2 && !isOwner && !inCompany) {
      toast.error('You do not have access to this Poll');
      history.replace(`/`);
    }
  }, [data, company, dbUser, history]);

  const checkPassword = () => {
    if (data?.poll.password === undefined) return true;
    if (data?.poll.owner === dbUser?.username) return true;

    if (data.poll.password === password) {
      setAuth(true);
      setPassword('');
      setPasswordError(undefined);
      toast.success('Verified Poll password.');
    } else {
      toast.error('Invalid Poll password.');
      setPasswordError('Invalid Poll password.');
      setAuth(false);
    }
  };

  if (auth === undefined) {
    data = undefined;
  }

  let bgColorBtnEditViewPoll = `${themeData.colors['detailPoll-btnViewEditPoll-color']}`;
  let borderColorBtnEditViewPoll = `${themeData.colors['detailPoll-btnViewEditPoll-borderColor']}`;
  let textColorBtnEditViewPoll = `${themeData.colors['detailPoll-btnViewEditPoll-textColor']}`;

  let bgColorBtnPledgeCastVote = `${themeData.colors['poll-btnPledgeCastVote-color']}`;
  let borderColorBtnPledgeCastVote = `${themeData.colors['poll-btnPledgeCastVote-borderColor']}`;
  let textColorBtnPledgeCastVote = `${themeData.colors['poll-btnPledgeCastVote-textColor']}`;

  let content;
  let leftContent;
  let header;
  if (error?.code === 'NotFound') {
    header = (
      <div className="pt-32">
        <div className="flex flex-col items-center justify-center px-4 py-24 mx-4 text-center bg-white shadow-r-salmon-md">
          <h1 className="text-3xl sm:text-4xl">Poll is no longer available.</h1>
          <p>The poll has been deleted or this is an invalid poll.</p>

          <div className="mt-24">
            <img src={Logo} alt="migthypoll" />
          </div>
        </div>
      </div>
    );
  } else if (data === undefined) {
    header = (
      <div className="pt-64 text-white">
        <Loader spinnerCn="bg-white" title="Fetching Poll information..." />
      </div>
    );
  } else if (auth !== true) {
    header = (
      <div className="pt-32">
        <div className="flex flex-col items-center justify-center px-4 py-24 mx-4 text-center bg-white shadow-r-salmon-md">
          <h1 className="text-3xl sm:text-4xl">Poll Password Protected</h1>
          <p>The poll is password protected, enter password to view.</p>

          <InputText
            className="max-w-sm mt-8"
            type="password"
            placeholder="Poll Password"
            value={password}
            onChange={(v) => {
              setPassword(v);
              setPasswordError(undefined);
            }}
          />
          {passwordError !== undefined && (
            <div className="flex justify-end px-2 mt-2 text-sm font-semibold text-red">
              {passwordError}
            </div>
          )}
          <div className="mt-8">
            <ButtonMighty
              color={bgColorBtnEditViewPoll}
              borderColor={borderColorBtnEditViewPoll}
              textColor={textColorBtnEditViewPoll}
              text="View Poll"
              neon={randomColors}
              margin="0"
              onClick={checkPassword}
            />
          </div>
        </div>
      </div>
    );
  } else {
    header = (
      <>
        <HeaderImage
          title={data?.poll.title || ''}
          titleSize={(data?.poll?.title?.length || 0) > 15 ? 40 : 55}
          image={data?.poll.image?.url || 'image'}
          alt="poll picture"
          pledge={!!data?.poll.hasPledge}
        />
      </>
    );
    leftContent = (
      <>
        <div className="px-4 lg:px-4 lg:mb-4 lg:mx-4 md:px-8">
          <div className="pt-8 pb-12">
            <div
              className="p-4"
              style={
                data?.poll.status === 'CREATED'
                  ? { border: `2px solid ` + randomColors }
                  : {}
              }
            >
              <div
                className="font-bold text-lg"
                style={{ color: randomColors }}
              >
                Vote Period
              </div>
              <div style={{ color: randomColors }}>Starts</div>
              <div className="text-white ">
                {format(data?.poll.startDate, 'dd LLL yyyy')}
              </div>
              <div className="text-white">
                {format(data?.poll.startDate, 'hh.mm aa')}
              </div>
              <div style={{ color: randomColors }} className=" mt-4">
                Ends
              </div>
              <div className="text-white ">
                {format(data?.poll.endDate, 'dd LLL yyyy')}
              </div>
              <div className="text-white">
                {format(data?.poll.endDate, 'hh.mm aa')}
              </div>
            </div>

            {data?.poll.pledgePeriod1StartDate &&
              data?.poll.pledgePeriod1EndDate && (
                <div
                  className="p-4 mt-4"
                  style={
                    data?.poll.status === 'PLEDGING1'
                      ? { border: `2px solid ` + randomColors }
                      : {}
                  }
                >
                  <div
                    className="font-bold text-lg"
                    style={{ color: randomColors }}
                  >
                    Pledge Period
                  </div>
                  <div style={{ color: randomColors }}>Starts</div>
                  <div className="text-white ">
                    {format(data?.poll.pledgePeriod1StartDate, 'dd LLL yyyy')}
                  </div>
                  <div className="text-white">
                    {format(data?.poll.pledgePeriod1StartDate, 'hh.mm aa')}
                  </div>
                  <div style={{ color: randomColors }} className=" mt-4">
                    Ends
                  </div>
                  <div className="text-white ">
                    {format(data?.poll.pledgePeriod1EndDate, 'dd LLL yyyy')}
                  </div>
                  <div className="text-white">
                    {format(data?.poll.pledgePeriod1EndDate, 'hh.mm aa')}
                  </div>
                </div>
              )}
            {/* <div
              className="flex flex-col items-center space-y-4"
              style={!data?.poll ? { minHeight: 158 } : {}}
            >
              {data?.poll.startDate && data?.poll.endDate && (
                <PeriodTimeline
                  title="Vote Period"
                  active={
                    data?.poll.startDate &&
                    data?.poll.endDate &&
                    isWithinInterval(new Date(), {
                      start: data?.poll.startDate,
                      end: data?.poll.endDate,
                    })
                  }
                  start={data?.poll.startDate}
                  end={data?.poll.endDate}
                />
              )}
              {data?.poll.pledgePeriod1StartDate &&
                data?.poll.pledgePeriod1EndDate && (
                  <PeriodTimeline
                    title="Pledge Period"
                    active={
                      data?.poll.pledgePeriod1StartDate &&
                      data?.poll.pledgePeriod1EndDate &&
                      isWithinInterval(new Date(), {
                        start: data?.poll.pledgePeriod1StartDate,
                        end: data?.poll.pledgePeriod1EndDate,
                      })
                    }
                    start={data?.poll.pledgePeriod1StartDate}
                    end={data?.poll.pledgePeriod1EndDate}
                  />
                )}
            </div> */}
          </div>
          {(data?.poll.description || data?.poll.video) && (
            <div className="flex flex-col space-y-4">
              <h2 className="text-3xl ">
                <span className="inline-block px-2 py-1 text-white">
                  about this poll
                </span>
              </h2>
              {(data?.poll.description || data?.poll.video) && (
                <p
                  className="text-base break-words whitespace-pre-wrap overflow-ellipsis"
                  style={{
                    color: randomColors,
                    hyphens: 'auto',
                    paddingBottom: data?.poll.video ? 'calc(32px + 64px)' : 32,
                  }}
                >
                  {data?.poll.description}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex-col hidden px-4 lg:flex">
          <PollShareCard
            pollId={pollId}
            owner={data?.poll.company?.name || data?.poll.owner || undefined}
          />
        </div>

        <div className="flex flex-col items-center">
          {data?.poll.endDate &&
            isFuture(data?.poll.endDate) &&
            ownerPermission && (
              <div className="hidden mx-4 mt-8 lg:flex">
                <Link to={`/poll/${pollId}/edit`}>
                  <ButtonMighty
                    neon={randomColors}
                    color={bgColorBtnEditViewPoll}
                    borderColor={borderColorBtnEditViewPoll}
                    textColor={textColorBtnEditViewPoll}
                    text="Edit Poll"
                    margin="0"
                  />
                </Link>
              </div>
            )}
          {data?.poll.pledgePeriod1StartDate &&
            isPast(data?.poll.pledgePeriod1StartDate) &&
            ownerPermission && (
              <div className="hidden mx-4 mt-8 lg:flex">
                <Link to={`/poll/${pollId}/pledges`}>
                  <ButtonMighty
                    neon={randomColors}
                    color={bgColorBtnPledgeCastVote}
                    borderColor={borderColorBtnPledgeCastVote}
                    textColor={textColorBtnPledgeCastVote}
                    text="View Pledges"
                    margin="0"
                  />
                </Link>
              </div>
            )}
        </div>
      </>
    );
    content = (
      <>
        {data?.poll.video && (
          <div className="z-10 -mt-16 lg:mt-4">
            <div className="flex items-center justify-center">
              <Embed
                className="z-20 w-full max-w-screen-sm mx-8 md:w-full shadow-l-magenta-lg"
                style={{ maxHeight: 360 }}
                src={data?.poll.video || ''}
              />
            </div>
          </div>
        )}

        <PollOptionsForm
          withdrawUrl={`${url}/withdraw`}
          poll={data?.poll}
          neon={randomColors}
          subPolls={data?.subPolls}
          selected={{}}
        />

        {data?.poll && (
          <div className="poll-detail-votes">
            <div>{voteCount}</div>
            <div>Total Number of Votes</div>
          </div>
        )}

        <div className="px-4 lg:hidden">
          <PollShareCard
            pollId={pollId}
            owner={data?.poll.company?.name || data?.poll.owner || undefined}
          />
        </div>

        <div className="mt-8 text-center underline text-detailPoll-reportLink-color">
          <Link to={`/poll/${pollId}/report`}>
            <span className="p-4">Report Poll</span>
          </Link>
        </div>

        {data?.poll.endDate && isFuture(data?.poll.endDate) && ownerPermission && (
          <div className="mx-4 mt-8 lg:hidden">
            <Link to={`/poll/${pollId}/edit`}>
              <ButtonMighty
                neon={randomColors}
                color={bgColorBtnEditViewPoll}
                borderColor={borderColorBtnEditViewPoll}
                textColor={textColorBtnEditViewPoll}
                text="Edit Poll"
                margin="0"
              />
            </Link>
          </div>
        )}

        {data?.poll.pledgePeriod1StartDate &&
          isPast(data?.poll.pledgePeriod1StartDate) &&
          ownerPermission && (
            <div className="mx-4 mt-8 lg:hidden">
              <Link to={`/poll/${pollId}/pledges`}>
                <ButtonMighty
                  neon={randomColors}
                  color={bgColorBtnPledgeCastVote}
                  borderColor={borderColorBtnPledgeCastVote}
                  textColor={textColorBtnPledgeCastVote}
                  text="View Pledges"
                  margin="0"
                />
              </Link>
            </div>
          )}
      </>
    );
  }

  return (
    <div
      className={`h-screen overflow-x-hidden overflow-y-scroll bg-fixed bg-black `}
    >
      {header}
      <div className="flex flex-col gap-4 mx-auto lg:container xl:gap-4 lg:flex-row">
        <div
          className="w-full my-4 lg:min-h-screen lg:sticky lg:top-0"
          style={{ maxWidth: 350 }}
        >
          {leftContent}
        </div>
        <div className="flex flex-1">
          <div className="flex-1 pb-24 md:pb-64 lg:pb-32 lg:mr-4 lg:pt-4">
            {content}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PollDetailPage;
